import React, { useMemo, useCallback, useState, useDeferredValue } from "react";
import { AgGridReact } from "@ag-grid-community/react";
import { ColDef, ICellRendererParams } from "@ag-grid-community/core";
import { Task, getTaskStatus } from "./types";
import { EmptyState } from "./components/EmptyState";
import { AssignTaskModal } from "./AssignTaskModal";
import { ConfirmationModal } from "./components/ConfirmationModal";
import { StatusBadge } from "./components/StatusBadge";
import { UserDisplay } from "./components/UserAvatar";
import { TaskNameDisplay } from "./components/TaskNameDisplay";
import { NumberBadge } from "./components/NumberBadge";
import { TaskTableSkeleton } from "./components/TaskTableSkeleton";
import { useQueryClient } from "@tanstack/react-query";
import { useTaskMutations } from "./hooks/useTaskMutations";
import { useAssignTask } from "./hooks/useAssignTask";
import { getCompletedCount } from "./utils/taskUtils";
import { GRID_CONFIG } from "./utils/constants";
import { taskQueryKeys } from "./utils/queryKeys";
import { Box, Pagination, TablePagination } from "@mui/material";
import "./utils/ag-grid-theme.css";

interface ActionsCellProps extends ICellRendererParams {
    onView: (task: Task) => void;
    onEdit: (task: Task) => void;
}

const ActionsCell: React.FC<ActionsCellProps> = ({ data, onEdit }) => {
    const task = data as Task;
    const queryClient = useQueryClient();
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const refreshList = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: taskQueryKeys.all });
    }, [queryClient]);

    const { cloneTaskMutation, deleteTaskMutation } = useTaskMutations({
        onRefresh: refreshList,
    });

    const {
        showAssignModal,
        setShowAssignModal,
        handleAssign,
        handleAssignClick,
    } = useAssignTask({
        task,
        onSuccess: refreshList,
    });

    const handleCloneConfirm = useCallback(() => {
        cloneTaskMutation.mutate(task.id);
    }, [cloneTaskMutation, task.id]);

    const handleDeleteConfirm = useCallback(() => {
        deleteTaskMutation.mutate(task.id);
    }, [deleteTaskMutation, task.id]);

    const actions = [
        {
            label: "Assign",
            icon: "fa-user-plus",
            onClick: handleAssignClick,
            classes:
                "bg-blue-50 border-blue-100 text-blue-600 hover:bg-blue-100",
            showLabel: true,
        },
        {
            label: "Edit",
            icon: "fa-pen",
            onClick: () => onEdit(task),
            classes:
                "bg-indigo-50 border-indigo-100 text-indigo-600 hover:bg-indigo-100",
            showLabel: false,
        },
        {
            label: "Clone",
            icon: "fa-copy",
            onClick: () => setShowCloneModal(true),
            classes:
                "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100",
            showLabel: false,
        },
        {
            label: "Delete",
            icon: "fa-trash",
            onClick: () => setShowDeleteModal(true),
            classes: "bg-red-50 border-red-200 text-red-600 hover:bg-red-100",
            showLabel: false,
        },
    ];

    return (
        <>
            <div
                className="flex items-center justify-start h-full px-1 gap-2"
                onClick={(e) => e.stopPropagation()}
            >
                {actions.map((action) => (
                    <button
                        key={action.label}
                        className={`flex items-center justify-center rounded-full border text-xs transition-colors gap-1.5 ${
                            action.showLabel ? "px-3 py-1.5 h-auto" : "w-8 h-8"
                        } ${action.classes}`}
                        onClick={action.onClick}
                        title={action.showLabel ? undefined : action.label}
                    >
                        <i className={`fas ${action.icon}`} />
                        {action.showLabel && (
                            <span className="text-xs font-medium">
                                {action.label}
                            </span>
                        )}
                    </button>
                ))}
            </div>
            <AssignTaskModal
                isOpen={showAssignModal}
                onClose={() => setShowAssignModal(false)}
                task={task}
                onAssign={handleAssign}
            />
            <ConfirmationModal
                isOpen={showCloneModal}
                onClose={() => setShowCloneModal(false)}
                onConfirm={handleCloneConfirm}
                title="Clone Task"
                message={`Are you sure you want to clone "${task?.node?.display || task?.more?.name || "this task"}"? A new task will be created with the same settings.`}
                confirmText="Clone"
                cancelText="Cancel"
                confirmButtonColor="primary"
            />
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                title="Delete Task"
                message={`Are you sure you want to delete "${task?.node?.display || task?.more?.name || "this task"}"? This action cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonColor="error"
            />
        </>
    );
};

interface TaskTableProps {
    tasks: Task[];
    isLoading: boolean;
    onTaskView: (task: Task) => void;
    onTaskEdit: (task: Task) => void;
    onCreateTask?: () => void;
    pageSize?: number;
    currentPage?: number;
    totalItems?: number;
    onPageChange?: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    serverSidePagination?: boolean;
}

export const TaskTable: React.FC<TaskTableProps> = ({
    tasks,
    isLoading,
    onTaskView,
    onTaskEdit,
    onCreateTask,
    pageSize = 10,
    currentPage = 1,
    totalItems = 0,
    onPageChange,
    onPageSizeChange,
    serverSidePagination = false,
}) => {
    // Defer large dataset updates to prevent UI blocking
    const deferredTasks = useDeferredValue(tasks);
    const isPending = deferredTasks !== tasks;
    
    // Column definitions
    const columnDefs = useMemo<ColDef[]>(
        () => [
            {
                headerName: "Task",
                field: "node.display",
                flex: 2,
                minWidth: 150,
                cellRenderer: (params: ICellRendererParams) => {
                    const task = params.data as Task;
                    return (
                        <div className="flex flex-col justify-center h-full py-2">
                            <TaskNameDisplay task={task} />
                        </div>
                    );
                },
                sortable: true,
            },
            {
                headerName: "Assigned To",
                field: "user.name",
                flex: 1,
                minWidth: 150,
                cellRenderer: (params: ICellRendererParams) => {
                    const task = params.data as Task;
                    return (
                        <div className="flex items-center h-full">
                            <UserDisplay
                                user={task.user}
                                avatarSize="sm"
                                nameClassName="text-slate-700"
                                phoneClassName="text-slate-400"
                            />
                        </div>
                    );
                },
                sortable: true,
            },
            {
                headerName: "Count",
                field: "count.count",
                flex: 1,
                minWidth: 80,
                cellRenderer: (params: ICellRendererParams) => {
                    const task = params.data as Task;
                    return <NumberBadge value={task.count?.count || 0} />;
                },
                sortable: true,
            },
            {
                headerName: "Status",
                field: "status",
                flex: 1,
                minWidth: 100,
                cellRenderer: (params: ICellRendererParams) => {
                    const task = params.data as Task;
                    return (
                        <div className="flex items-center h-full">
                            <StatusBadge
                                task={task}
                                size="sm"
                                showIcon={false}
                            />
                        </div>
                    );
                },
                sortable: true,
                comparator: (valueA, valueB, nodeA, nodeB) => {
                    const statusA = getTaskStatus(nodeA?.data as Task);
                    const statusB = getTaskStatus(nodeB?.data as Task);
                    const order = {
                        "not-started": 0,
                        "in-progress": 1,
                        completed: 2,
                    };
                    return (order[statusA] || 0) - (order[statusB] || 0);
                },
            },
            {
                headerName: "Completed",
                field: "count.count - status.pending",
                flex: 1,
                minWidth: 80,
                cellRenderer: (params: ICellRendererParams) => {
                    const task = params.data as Task;
                    const completedCount = getCompletedCount(task);
                    return <NumberBadge value={completedCount} />;
                },
                sortable: true,
            },
            {
                headerName: "Valid",
                field: "status.by_qastatus.valid",
                flex: 1,
                minWidth: 80,
                cellRenderer: (params: ICellRendererParams) => {
                    const task = params.data as Task;
                    return (
                        <NumberBadge
                            value={task.status?.by_qastatus?.valid || 0}
                        />
                    );
                },
                sortable: true,
            },
            {
                headerName: "Invalid",
                field: "status.by_qastatus.invalid",
                flex: 1,
                minWidth: 80,
                cellRenderer: (params: ICellRendererParams) => {
                    const task = params.data as Task;
                    return (
                        <NumberBadge
                            value={task.status?.by_qastatus?.invalid || 0}
                        />
                    );
                },
                sortable: true,
            },
            {
                headerName: "Actions",
                field: "actions",
                flex: 1,
                minWidth: 250,
                cellRenderer: (params: ICellRendererParams) => (
                    <ActionsCell
                        {...params}
                        onView={onTaskView}
                        onEdit={onTaskEdit}
                    />
                ),
                sortable: false,
                suppressMovable: true,
                cellClass: "ag-cell-actions",
            },
        ],
        [onTaskView, onTaskEdit]
    );

    // Default column settings
    const defaultColDef = useMemo<ColDef>(
        () => ({
            resizable: true,
            suppressMovable: true,
        }),
        []
    );

    // Row click handler
    const handleRowClicked = useCallback(
        (event: any) => {
            if (event.data) {
                const target = event.event?.target as HTMLElement;
                if (target && !target.closest(".ag-cell-actions")) {
                    onTaskView(event.data as Task);
                }
            }
        },
        [onTaskView]
    );

    // Row style handler
    const getRowStyle = () => ({
        cursor: "pointer",
    });

    // Calculate grid height - use fixed max height to enable row virtualization
    const gridHeight = useMemo(() => {
        const rowsToRender = deferredTasks.length;
        const padding = Math.max(8, GRID_CONFIG.PADDING - 10);
        // Calculate ideal height based on rows
        const idealHeight = GRID_CONFIG.HEADER_HEIGHT +
            GRID_CONFIG.ROW_HEIGHT * Math.max(1, rowsToRender) +
            padding;
        // Use fixed max height to enable row virtualization for large datasets
        const height = Math.min(idealHeight, GRID_CONFIG.MAX_VISIBLE_HEIGHT);
        return height;
    }, [deferredTasks.length]);

    if (isLoading || isPending) {
        return <TaskTableSkeleton pageSize={pageSize} />;
    }

    if (!deferredTasks || deferredTasks.length === 0) {
        return (
            <EmptyState
                title="No tasks yet"
                description="Create your first task to start managing field work and surveys"
                icon="fa-tasks"
                action={
                    onCreateTask
                        ? { label: "Create Task", onClick: onCreateTask }
                        : undefined
                }
            />
        );
    }

    // Calculate pagination info (for server-side mode)
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endIndex = Math.min(currentPage * pageSize, totalItems);

    return (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div
                className="ag-theme-quartz"
                style={{
                    width: "100%",
                    height: `${gridHeight}px`,
                }}
            >
                <AgGridReact
                    rowData={deferredTasks}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    rowHeight={GRID_CONFIG.ROW_HEIGHT}
                    headerHeight={GRID_CONFIG.HEADER_HEIGHT}
                    suppressHorizontalScroll={false}
                    suppressRowClickSelection={true}
                    animateRows={false}
                    suppressCellFocus={true}
                    suppressRowHoverHighlight={false}
                    onRowClicked={handleRowClicked}
                    getRowStyle={getRowStyle}
                    getRowId={(params) => String(params.data.id)}
                    rowBuffer={5}
                    debounceVerticalScrollbar={true}
                    suppressScrollOnNewData={true}
                    suppressRowVirtualisation={false}
                />
            </div>

            {/* MUI pagination for server-side mode */}
            {serverSidePagination && onPageChange && onPageSizeChange && (
                <div className="border-t border-slate-200">
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            px: 1,
                        }}
                    >
                        <TablePagination
                            component="div"
                            count={totalItems}
                            page={Math.max(0, currentPage - 1)}
                            onPageChange={(_, nextPage) =>
                                onPageChange(nextPage + 1)
                            }
                            rowsPerPage={pageSize}
                            onRowsPerPageChange={(e) =>
                                onPageSizeChange(Number(e.target.value))
                            }
                            rowsPerPageOptions={[10, 20, 30, 40, 50, 100]}
                            labelDisplayedRows={() =>
                                totalItems === 0
                                    ? "0 of 0"
                                    : `${startIndex}-${endIndex} of ${totalItems}`
                            }
                            labelRowsPerPage="Rows per page:"
                        />

                        {/* Page numbers */}
                        {totalItems > 0 && (
                            <Pagination
                                count={totalPages}
                                page={Math.min(currentPage, totalPages)}
                                onChange={(_, page) => onPageChange(page)}
                                showFirstButton
                                showLastButton
                                color="primary"
                                size="small"
                            />
                        )}
                    </Box>
                </div>
            )}
        </div>
    );
};
